import { FC, useContext, useEffect, useState, } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";

import CaseInformation from "src/components/caseInformation";
import CaseNotes from "src/components/caseNotes";
import DocumentUpload from "src/components/documentUpload";
import FooterText from "src/components/footerText";
import { CaseNotesType } from "src/components/caseNotes/types";
import InsuranceInformation from "src/components/insuranceInformation";
import PatientInformation from "src/components/patientInformation";
import PrescriberInformation from "src/components/prescriberInformation";

import useFetch from "src/hooks/useFetch";
import { LoaderContext } from "src/context/loaderContext";

import { CaseDetailType } from '../../../src/types/caseDetail';
import { CaseDetailAPIResponseType } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import {TextAlignProps} from "../../components/contactAndSafetyInfo/types";
import ContactAndSafetyInfo from "../../components/contactAndSafetyInfo";


const HubCaseDetail: FC = (): JSX.Element => {
  const { caseId } = useParams();
  const { hideLoader } = useContext(LoaderContext);
  const [caseNotes, setCaseNotes] = useState<CaseNotesType[]>([]);
  const [caseDetail, setCaseDetail] = useState<CaseDetailType>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const extraParam = searchParams.get("demo");

  const formatPhoneNumber = (phoneNumber: number | string) => {
    const cleaned = phoneNumber.toString().replace(/\D/g, "");
    if (cleaned.length !== 10) {
      return phoneNumber.toString(); // Return as-is if not valid
    }
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const { data: caseDetailObject } = useFetch<CaseDetailAPIResponseType>(
    APIEndpoints.CASE_DETAIL,
    { open : extraParam === "true" ? 'hidden' : '' },
    { caseId }
  );

  useEffect(() => {
    if (caseDetailObject) {
      hideLoader();
      setCaseNotes(caseDetailObject?.data.case_notes);
      setCaseDetail(caseDetailObject?.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseDetailObject]);

  const onreInvestigate = () => {
    navigate(`/cases/${caseId}/clone`)
  };

  return (
    <div className="bg-light overflow-auto flex-fill">
      <div className="container pt-3">
        <ContactAndSafetyInfo alignText={TextAlignProps.TEXT_END} textColor="black"/>
        { caseDetailObject && caseDetailObject.data && caseDetailObject.data.case_information && caseDetailObject.data.case_information.reinvestigation &&
            <div className="alert alert-success d-flex align-items-center flex-wrap">
              <h2 className="m-0" style={{color:'#155724', fontSize:'1.875rem'}}>
                This patient may be eligible for retreatment! Click here to reinvestigate:
              </h2>
              <button className="btn btn-lg btn-primary ms-1" onClick={onreInvestigate} style={{fontSize:'18px'}}>
                <i className="bi bi-arrow-clockwise"></i> Re-investigate
              </button>
            </div>
        }

        <div className="mt-4 row mb-2">
          <div className={"col-lg-6 col-12 d-flex"}>
            <h2 className="text-primary m-0 flex-grow-1 d-flex align-items-center" style={{ fontSize: "1.875rem"}}>Case# {caseId}</h2>
          </div>
          <div className={"col-lg-6 col-12 d-flex"}>
            <h1 className={`m-0 text-center text-white py-2 flex-grow-1 background-color-${
                caseDetail && caseDetail.case_information
                  ? caseDetail.case_information.color
                  : 1
              }`}
              style={{ fontSize: "1.875rem" }}
            >
              {caseDetail && caseDetail.case_information
                ? caseDetail.case_information.status
                : ""}
            </h1>
          </div>
        </div>

        {caseDetail ? (
          <>
            {/* Case Information */}
            <div className="">
              <CaseInformation caseObject={caseDetail} />
            </div>

            {/* Documents */}
            <div className="my-4">
              <DocumentUpload
                setNotes={setCaseNotes}
                caseDocuments={
                  caseDetail.case_documents ? caseDetail.case_documents : []
                }
                caseId={
                  caseDetail.case_information
                    ? caseDetail.case_information.case_id
                    : 0
                }
              />
            </div>
            {/* Case Notes */}
            <div className="my-4">
              <CaseNotes
                caseNotes={caseNotes}
                setNotes={setCaseNotes}
                caseObject={caseDetail}
              />
            </div>
            {/* Patient Information */}
            <div className="my-4">
              <PatientInformation
                caseObject={caseDetail}
                formatPhoneNumber={formatPhoneNumber}
              />
            </div>
            {/* Prescriber Information */}
            <div className="my-4">
              <PrescriberInformation
                caseObject={caseDetail}
                formatPhoneNumber={formatPhoneNumber}
              />
            </div>
            {/* Insurance Information */}
            <div className="card">
              <div className="card-header">
                <h4 className="m-0">Insurance Information</h4>
              </div>

              <div className="card-body">
                {caseDetail && caseDetail.insurance_information && caseDetail.insurance_information.length > 0 ? (
                  <>
                    {caseDetail.insurance_information.map((item, index) => (
                      <InsuranceInformation
                        key={index}
                        insuranceNumber={index + 1}
                        insuranceInformation={item}
                        formatPhoneNumber={formatPhoneNumber}
                      />
                    ))}
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : null}

        <div className="mt-3">
          <FooterText />
        </div>
      </div>
    </div>
  );
};

export default HubCaseDetail;
